import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUser, faList } from '@fortawesome/free-solid-svg-icons';
import { fetchAdmins, deleteAdmin } from '../../../store/slices/adminSlice';
import adminService from '../../../services/adminService';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import SharedDetailModal from '../../../components/Common/SharedDetailModal/SharedDetailModal';
import DetailsPage from '../../../components/Common/DetailsPage/DetailsPage';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import './AdminManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';
import { normalizeTimeZone } from '../../../utils/constants';

const normalizeAdminStatus = (s) => {
  const v = (s || 'Active').toString().toLowerCase();
  return v === 'active' ? 'Active' : 'Inactive';
};

const AdminManagement = ({ practiceId, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clearBreadcrumbs } = useBreadcrumb();
  const {
    admins,
    adminsByPracticeId,
    practiceAdminFetched,
    globalAdminsFetched,
    isLoading,
    error
  } = useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);

  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [loadingAllAdmins, setLoadingAllAdmins] = useState(false);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');
  const [optimisticDeletedIds, setOptimisticDeletedIds] = useState([]);

  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');

  const pidKey = practiceId != null ? String(practiceId) : null;
  const hasPracticeCache = pidKey && practiceAdminFetched[pidKey];
  const practiceAdminsList = pidKey ? (adminsByPracticeId[pidKey] ?? []) : null;
  const adminsSource =
    practiceId != null
      ? hasPracticeCache
        ? practiceAdminsList
        : null
      : admins;

  useEffect(() => {
    if (!practiceId) {
      clearBreadcrumbs();
    }
  }, [clearBreadcrumbs, practiceId]);

  useEffect(() => {
    if (practiceId) {
      if (!practiceAdminFetched[String(practiceId)]) {
        dispatch(fetchAdmins({ practiceId }));
      }
    } else {
      if (!globalAdminsFetched) {
        fetchAllAdmins();
      }
    }
  }, [practiceId, dispatch, practiceAdminFetched, globalAdminsFetched]);

  const fetchAllAdmins = async () => {
    try {
      setLoadingAllAdmins(true);
      await dispatch(fetchAdmins({ practiceId: null, forceRefresh: true })).unwrap();
    } catch (e) {
      console.error('Error fetching all admins:', e);
    } finally {
      setLoadingAllAdmins(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [localSearchTerm, searchTerm]);

  useEffect(() => {
    if (!adminsSource) {
      setFilteredAdmins([]);
      return;
    }
    let filtered = adminsSource.filter((admin) => !admin.deleted_at);
    if (localSearchTerm.trim()) {
      const q = localSearchTerm.toLowerCase();
      filtered = filtered.filter((admin) => {
        const fullName = `${admin.first_name || ''} ${admin.last_name || ''}`.toLowerCase();
        return (
          fullName.includes(q) ||
          (admin.email || '').toLowerCase().includes(q) ||
          (admin.phone || '').toLowerCase().includes(q)
        );
      });
    }
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'idx') {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return sortOrder === 'asc' ? ai - bi : bi - ai;
      }
      if (sortBy === 'status') {
        const o = { active: 1, inactive: 2 };
        const sa = (a.status || 'Active').toLowerCase();
        const sb = (b.status || 'Active').toLowerCase();
        const orderA = o[sa] || 3;
        const orderB = o[sb] || 3;
        if (orderA !== orderB) {
          return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
        }
        return 0;
      }
      if (sortBy === 'name') {
        const na = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nb = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return sortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
      }
      if (sortBy === 'address') {
        const va = formatAddressForTable(a).toLowerCase();
        const vb = formatAddressForTable(b).toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'home') {
        const va = String(a.home_phone || a.phone || '').toLowerCase();
        const vb = String(b.home_phone || b.phone || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'cell') {
        const va = String(a.cell_phone || '').toLowerCase();
        const vb = String(b.cell_phone || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'fax') {
        const va = String(a.fax || '').toLowerCase();
        const vb = String(b.fax || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'email') {
        const va = String(a.email || '').toLowerCase();
        const vb = String(b.email || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const va = a[sortBy] || '';
      const vb = b[sortBy] || '';
      return sortOrder === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    // Remove optimistic deletes
    filtered = filtered.filter(admin => !optimisticDeletedIds.includes(admin.id));

    setFilteredAdmins(filtered);
  }, [adminsSource, localSearchTerm, sortBy, sortOrder, optimisticDeletedIds]);

  const entriesPerPageNum = entriesPerPage === 'all' ? filteredAdmins.length : parseInt(entriesPerPage, 10);
  const totalPages = entriesPerPage === 'all' || !entriesPerPageNum ? 1 : Math.ceil(filteredAdmins.length / entriesPerPageNum) || 1;
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const currentAdmins =
    entriesPerPage === 'all' ? filteredAdmins : filteredAdmins.slice(startIndex, startIndex + entriesPerPageNum);

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const formatAddressForTable = (admin) => {
    if (!admin) return 'N/A';
    const primaryLine =
      admin.address_line_1 || admin.address?.address_line_1 || admin.address_1 || '';
    return primaryLine && String(primaryLine).trim() ? String(primaryLine).trim() : 'N/A';
  };

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const getAdminDisplayName = (data) => {
    const first = data?.first_name || '';
    const last = data?.last_name || '';
    if (last && first) return `${last}, ${first}`;
    return `${first} ${last}`.trim() || 'N/A';
  };

  const formatAdminAddressLines = (data) => {
    if (!data) return ['N/A'];
    const addr = data.address || {};
    return buildDetailAddressLines({
      line1: data.address_line_1 ?? addr.address_line_1 ?? data.address_1,
      line2: data.address_line_2 ?? addr.address_line_2 ?? data.address_2,
      city: data.city ?? addr.city,
      stateDisplay: resolveStateDisplay(data),
      zip: data.zip_code ?? data.zipCode ?? addr.zip_code ?? addr.zip
    });
  };

  const handleAddAdmin = () => {
    if (practiceId) navigate(`/practices/${practiceId}/add-admin`);
    else navigate('/admin/add');
  };

  const handleViewAdmin = (admin) => {
    const params = new URLSearchParams(window.location.search);
    params.set('viewId', admin.id);
    navigate({ search: params.toString() });
  };

  const closeAdminDetails = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() });
  };

  const handleEditAdmin = (adminId) => {
    if (practiceId) navigate(`/practices/${practiceId}/add-admin?editId=${adminId}`);
    else navigate(`/admin/add?editId=${adminId}`);
  };

  const handleDeleteAdmin = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;
    const idToDelete = adminToDelete.id;
    try {
      // Optimistic UI: Remove from list and close modal immediately
      setOptimisticDeletedIds(prev => [...prev, idToDelete]);
      setShowDeleteModal(false);
      setAdminToDelete(null);
      toast.success(practiceId ? 'Practice admin deleted' : 'System admin deleted');

      if (practiceId) {
        await dispatch(deleteAdmin({ practiceId, adminId: idToDelete })).unwrap();
      } else {
        await adminService.deleteSystemAdmin(idToDelete);
        await fetchAllAdmins();
      }
    } catch (err) {
      // Rollback on error
      setOptimisticDeletedIds(prev => prev.filter(id => id !== idToDelete));

      console.error('Error deleting admin:', err);
      if (practiceId) {
        await dispatch(fetchAdmins({ practiceId })).catch(() => { });
      } else {
        await fetchAllAdmins();
      }

      const msg = err?.message || err || '';
      if (msg.includes('already') || msg.includes('not found')) {
        toast.info('Admin not found or already deleted');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Failed to delete admin');
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const columns = useMemo(
    () => [
      {
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, rowIndex) => <span>{startIndex + rowIndex + 1}</span>
      },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={normalizeAdminStatus(row.status)} />
      },
      {
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="admin-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewAdmin(row);
            }}
          >
            {`${row.last_name || ''}, ${row.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      },
      { key: 'address', title: 'Address', sortable: true, render: (row) => formatAddressForTable(row) },
      { key: 'home', title: 'Home Phone', sortable: true, render: (row) => row.home_phone || row.phone || 'N/A' },
      { key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => row.cell_phone || 'N/A' },
      { key: 'fax', title: 'Fax', sortable: true, render: (row) => row.fax || 'N/A' },
      { key: 'email', title: 'Email', sortable: true, render: (row) => row.email || 'N/A' },
      {
        key: 'action',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewAdmin(row)}
            onEdit={!isReadOnly ? () => handleEditAdmin(row.id) : null}
            onDelete={!isReadOnly ? () => handleDeleteAdmin(row) : null}
          />
        )
      }
    ],
    [startIndex, isReadOnly]
  );

  const showTableLoading =
    (practiceId && !hasPracticeCache && isLoading) || (!practiceId && (isLoading || loadingAllAdmins));

  const adminDetails = useMemo(() => {
    if (!viewId) return null;
    return adminsSource?.find(a => String(a.id) === String(viewId));
  }, [viewId, adminsSource]);

  const adminListReady = practiceId ? Boolean(hasPracticeCache) : Boolean(globalAdminsFetched);
  const staleDetailViewId =
    Boolean(viewId) &&
    adminListReady &&
    !showTableLoading &&
    adminDetails === undefined;

  useEffect(() => {
    if (!staleDetailViewId) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('viewId');
    navigate({ search: params.toString() }, { replace: true });
  }, [staleDetailViewId, navigate]);

  if (viewId && !staleDetailViewId) {
    return (
      <DetailsPage
        title={practiceId ? 'Practice Admin' : 'Admin'}
        data={adminDetails}
        isLoading={showTableLoading}
        config={[
          {
            placement: 'left',
            fields: [
              { label: 'Name', value: (data) => getAdminDisplayName(data) },
              { label: 'Address', value: (data) => formatAdminAddressLines(data), multiline: true },
              { label: 'Email', value: 'email' },
              { label: 'Home Phone', value: (data) => getSafeValue(data.home_phone || data.phone) },
              { label: 'Cell Phone', value: (data) => getSafeValue(data.cell_phone) },
              { label: 'Fax', value: (data) => getSafeValue(data.fax) }
            ]
          },
          {
            placement: 'right',
            fields: [
              { label: 'Username', value: (data) => data.username || data.user?.username || 'N/A' },
              {
                label: 'Two Way Authentication',
                value: (data) => {
                  const status = data.is_two_way_auth ?? data.two_way_authentication_enabled ?? data.two_way_auth ?? data.two_way_authentication;
                  if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
                  if (status === 0 || status === false || status === 'Disabled') return 'Disabled';
                  return 'N/A';
                }
              },
              {
                label: 'Time Zone',
                value: (data) => {
                  const raw = data?.time_zone ?? data?.timeZone;
                  const tz = normalizeTimeZone(raw);
                  return tz || raw || 'N/A';
                }
              }
            ]
          }
        ]}
      />
    );
  }

  return (
    <div className="admin-management-container">
      {error && !showTableLoading && (
        <div className="admin-management-alert admin-management-alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <div className="admin-management-header-row">
        <h2 className="page-title">
          {practiceId ? 'Practice Admin List' : 'Admin List'}
        </h2>

        <div className="admin-management-header-actions">
          <SearchBar
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            className="admin-management-search-wrapper"
          />
          {user?.role_id !== 4 && !isReadOnly && (
            <button type="button" className="list-screen-add-btn" onClick={handleAddAdmin}>
              <FontAwesomeIcon icon={faPlus} className="admin-management-add-icon" />
              Add Admin
            </button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={showTableLoading ? [] : currentAdmins}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={showTableLoading}
        loadingMode="spinner"
        emptyMessage=""
      />

      {!showTableLoading && currentAdmins.length === 0 && (
        <div className="admin-management-empty-below-table">
          <FontAwesomeIcon icon={faUser} className="admin-management-empty-icon" />
          <p>No admins found</p>
        </div>
      )}

      <div className="list-screen-pagination-section">
        <div className="list-screen-pagination-left">
          <div className="list-screen-entries-selector">
            <span>Show</span>
            <select
              className="list-screen-entries-select"
              style={{ width: '80px' }}
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="all">All</option>
            </select>
            <span>entries</span>
          </div>

          <button
            type="button"
            className="list-screen-option-btn"
            onClick={() =>
              navigate(
                practiceId
                  ? `/event-logs/Admin/all?practiceId=${practiceId}`
                  : "/event-logs/Admin/all"
              )
            }
          >
            <FontAwesomeIcon
              icon={faList}
              className="admin-management-add-icon"
            />
            Events Log
          </button>
        </div>

        <div className="list-screen-pagination-right">
          <div className="list-screen-page-selector">
            <span>Page</span>
            <select
              className="list-screen-page-select"
              style={{ width: "60px" }}
              value={Math.min(currentPage, totalPages)}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <option key={page} value={page}>
                  {page}
                </option>
              ))}
            </select>
            <span>of {totalPages}</span>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="modal-close" onClick={cancelDelete}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the admin{' '}
                <strong>
                  {adminToDelete?.first_name} {adminToDelete?.last_name}
                </strong>
                ?
              </p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button type="button" className="btn-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default AdminManagement;
